import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Dialog, Radio, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import parse, { Element, attributesToProps, HTMLReactParserOptions } from 'html-react-parser';
import { toast } from 'react-toastify';
import { API } from '../../../../../api';
import { useAppSelector } from '../../../../../redux/hooks';
import useEditingFrozen from '../../../../../hooks/useEditingFrozen';
import { EDITING_FROZEN_MESSAGE } from '../../../../../utils/constants';
import dosenFavicon from '../../../../../assets/images/brand/dosen/favicon.png';
import { GeneratedLesson, Material, MCQ, ElliottSections, ElliottSimulationRubric } from '../types';
import { getDecryptAns } from '../../../../../utils/decryptAns';
import LiveSimulationSection from './LiveSimulationSection';
import ActionPill from './ActionPill';
import ImproveWithAIModal from './ImproveWithAIModal';
import SectionToggle from './SectionToggle';
import { buttonDisabledSx, ctaButtonSx, secondaryButtonSx } from '../../../../../components/ui/Buttons/buttonStyles';
import ElliottWorking from '../../../../../components/ui/ElliottWorking';
import LessonVideoPlayer from '../../../../../components/LessonVideoPlayer';
import CapabilityChip from '../../../../../components/ui/CapabilityChip';
import { lessonCapability, useLessonTags } from '../lessonTags';
import CapabilityConfirm, { toastWithUndo, type CapabilityConfirmRequest } from './CapabilityConfirm';

type ElliottSectionKey = 'caseStudies' | 'simulation' | 'quiz';

/**
 * Lets LessonContentView persist / AI-generate its Elliott sections against
 * either the generation artifact (the AIMD wizard — default when omitted) or a
 * live template task (the post-creation design editor). When omitted the
 * component keeps its original artifact behaviour (via `generationId`).
 */
export interface ElliottSectionAdapter {
  /** false for a not-yet-saved lesson (no Mongo ObjectId ids) — AI is blocked. */
  ready: boolean;
  /** 'task' shows "Generate with AI" empty states (no background cascade spinner). */
  mode: 'artifact' | 'task';
  persistSection: (payload: {
    caseStudies?: { id: string; title: string; sections: { label: string; text: string }[] }[];
    /** null deletes the Roleplay (a lesson whose capability changed can't keep the old one). */
    simulationRubric?: { brief: string; skill: string; strongPoints: string[]; weakPoints: string[] } | null;
    simulationEnabled?: boolean;
    caseStudiesEnabled?: boolean;
    quizEnabled?: boolean;
  }) => Promise<void>;
  /**
   * Empty feedback = plain generate. Returns fresh section data to apply.
   * `caseStudyIndex` scopes a caseStudies improve to that one card.
   */
  improveSection: (
    section: ElliottSectionKey,
    feedback: string,
    caseStudyIndex?: number
  ) => Promise<{ elliott?: Partial<ElliottSections>; mcqs?: MCQ[] } | undefined>;
}

/**
 * A Capability build-flow lesson's Roleplay rules (docs/PRD-capability-lesson-roleplays.md
 * R9, D1, D2), given by the editor that knows the whole course. A Roleplay
 * switched on is a Scored Roleplay on the lesson's capability: an outcome
 * lesson picks a capability first, and the Roleplay is written there and then.
 * Omitted for any other program: the section works as it always has.
 */
export interface RoleplayPolicy {
  /** The capability the lesson builds; null when it serves outcomes. */
  capability: { id: string; name: string } | null;
  /** The outcomes it serves, when it builds no capability. */
  outcomes: string[];
  /** Turning this lesson's Roleplay off leaves its capability measured nowhere in the course. */
  lastScored: boolean;
  /** Another lesson whose Scored Roleplay still measures the capability. */
  stillMeasuredIn: string | null;
  /**
   * Opens the editor's capability picker and, on a pick, makes the lesson
   * build it and saves that. Resolves with the capability, or null on Cancel.
   */
  pickCapability: (reason: 'switchOn' | 'change') => Promise<{ id: string; name: string } | null>;
  /** The lesson's Scored Roleplay went on or off: the editor keeps its copy of the flag in step. */
  onScoredChange: (scored: boolean) => void;
}

interface LessonContentViewProps {
  /** A build-flow lesson's Roleplay rules (see RoleplayPolicy). */
  roleplayPolicy?: RoleplayPolicy;
  /** Rendered in line with the lesson title (e.g. collaborator avatars + Invite Admin). */
  headerAction?: React.ReactNode;
  /** Beside the lesson name (the program designer's Audience Requirements icon). */
  titleExtra?: React.ReactNode;
  /** The capability the lesson builds, shown beside its title. Without it, the build flow's lesson tags name it. */
  capabilityName?: string | null;
  lesson: GeneratedLesson;
  moduleName: string;
  moduleIndex: number;
  lessonIndex: number;
  /** Needed for the section-scoped "Improve with AI" endpoint (artifact mode). */
  generationId?: string | null;
  /** When provided, section persist/improve run against a live task instead of the artifact. */
  sectionAdapter?: ElliottSectionAdapter;
  onEdit: () => void;
  /** Omit to hide the lesson-content "Improve with AI" pill (design editor). */
  onImprove?: () => void;
  /** Omit to hide the "AI Reasoning" pill (design editor). */
  onReasoningClick?: () => void;
  /** Render the AI Reasoning pill disabled (no reasoning data yet — design
   *  editor on a never-improved manual lesson). */
  reasoningDisabled?: boolean;
  reasoningDisabledTooltip?: string;
  onMaterialUpdate?: (materialIndex: number, updated: Material) => void;
  onAssessmentsUpdate?: (mcqs: MCQ[]) => void;
  /** Provide to show the "Lesson Resources" section (add button opens the parent's picker). */
  onAddMaterial?: () => void;
  /** Provide to allow removing a resource from the list. */
  onRemoveMaterial?: (index: number) => void;
  /**
   * Fully-managed Lesson Resources section rendered directly below the Lesson
   * Content box (design editor). Takes precedence over the built-in
   * onAddMaterial section.
   */
  resourcesSlot?: React.ReactNode;
  // Lesson Video Builder card (program designer) — rendered under the content.
  videoSlot?: React.ReactNode;
  onGenerateImage?: () => void;
  onGenerateVideo?: () => void;
  onRemoveMedia?: (src: string, type: 'img' | 'video') => void;
  imageCount?: number;
  videoCount?: number;
  isGeneratingImage?: boolean;
  isGeneratingVideo?: boolean;
}

function getCorrectIndex(options: string[] | null | undefined, correctAnswer: string): number {
  if (!options || options.length === 0) return -1;

  // correct_answer may be encrypted (same format as correctAns on manual MCQs)
  try {
    const decrypted = getDecryptAns(correctAnswer);
    if (decrypted && decrypted !== correctAnswer) {
      const decryptedMatch = options.findIndex((o) => o.toLowerCase() === String(decrypted).toLowerCase());
      if (decryptedMatch >= 0) return decryptedMatch;
    }
  } catch {
    /* not encrypted */
  }

  const direct = options.findIndex((o) => o === correctAnswer);
  if (direct >= 0) return direct;

  try {
    const decoded = atob(correctAnswer);
    const decodedMatch = options.findIndex((o) => o === decoded);
    if (decodedMatch >= 0) return decodedMatch;
    const num = parseInt(decoded, 10);
    if (!isNaN(num) && num >= 0 && num < options.length) return num;
  } catch {
    /* not base64 */
  }

  const upperAnswer = correctAnswer.toUpperCase();
  if (upperAnswer.length === 1 && upperAnswer >= 'A' && upperAnswer <= 'Z') {
    const idx = upperAnswer.charCodeAt(0) - 65;
    if (idx >= 0 && idx < options.length) return idx;
  }

  const numIdx = parseInt(correctAnswer, 10);
  if (!isNaN(numIdx) && numIdx >= 0 && numIdx < options.length) return numIdx;

  const ci = options.findIndex((o) => o.toLowerCase() === correctAnswer.toLowerCase());
  if (ci >= 0) return ci;

  return -1;
}

/**
 * If the content has no block-level HTML tags it's likely plain text from the API.
 * Convert double newlines into paragraphs and single newlines into <br/>.
 */
function normalizeContentToHtml(raw: string): string {
  if (!raw) return '';
  const trimmed = raw.trim();

  // Already contains block-level HTML — return as-is
  if (/<(?:p|div|h[1-6]|ul|ol|li|table|blockquote|pre)[\s>/]/i.test(trimmed)) {
    return trimmed;
  }

  // Plain text → wrap paragraphs
  const paragraphs = trimmed.split(/\n{2,}/);
  return paragraphs.map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('');
}

const EMPTY_MCQ: MCQ = {
  question: '',
  options: ['', '', '', ''],
  correct_answer: '0',
  optionReasonings: ['', '', '', '']
};

// Elliott quiz target size — the learner quiz serves 3 questions per lesson; the
// BE tops existing MCQs up with Elliott-generated ones at publish. Mirror that here.
const ELLIOTT_QUIZ_QUESTION_COUNT = 3;

function buildMcqList(lesson: GeneratedLesson): MCQ[] {
  const raw = lesson.mcqs && lesson.mcqs.length > 0 ? lesson.mcqs : lesson.mcq ? [lesson.mcq] : [];
  const legacy = raw.map((m) => ({ ...m, options: m.options ?? [] }));
  // Top up with Elliott-generated questions (same rule the BE applies on publish).
  const extras = (lesson.elliott?.quiz || [])
    .slice(0, Math.max(0, ELLIOTT_QUIZ_QUESTION_COUNT - legacy.length))
    .map((q) => ({
      question: q.question,
      options: q.options ?? [],
      correct_answer: q.correct_answer || '',
      explanation: q.explanation,
      optionReasonings: q.optionReasonings
    }));
  return [...legacy, ...extras];
}

/** Renders a single image or video with a hover-triggered × remove button. */
const MediaWrapper: React.FC<{
  type: 'img' | 'video';
  src: string;
  attribs: Record<string, string>;
  onRemove: () => void;
}> = ({ type, src, attribs, onRemove }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      sx={{ position: 'relative', display: 'block', my: 2 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {type === 'img' ? (
        <img
          src={src}
          alt={attribs.alt || ''}
          style={{ maxWidth: '100%', height: 'auto', borderRadius: 8, display: 'block' }}
        />
      ) : (
        <LessonVideoPlayer src={src} />
      )}
      {hovered && (
        <Box
          component="button"
          onClick={onRemove}
          title="Remove"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.65)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '50%',
            cursor: 'pointer',
            color: '#fff',
            transition: 'background 0.15s',
            '&:hover': { background: '#c53030' }
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </Box>
      )}
    </Box>
  );
};

// Subtle outlined container that separates each editor section (Content / Case Studies / Live Sim / Quiz).
const sectionBoxSx = {
  border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
  borderRadius: '12px',
  background: 'rgba(255,255,255,0.02)',
  p: 2.5
} as const;

const BLANK_CASE_STUDY_SECTIONS = [
  { label: 'Scenario', text: '' },
  { label: 'Action', text: '' },
  { label: 'Relevance', text: '' }
];

interface CaseStudy {
  id: string;
  title: string;
  sections: { label: string; text: string }[];
}

// Structured case studies come from lesson.elliott (generated per-lesson on the BE).
// Fallback: derive placeholders from the legacy materials while the Elliott
// sections are still generating (empty Scenario/Action/Relevance shells).
function buildCaseStudies(l: GeneratedLesson): CaseStudy[] {
  const real = l.elliott?.caseStudies;
  if (real && real.length > 0) {
    return real.map((cs, i) => ({
      id: cs.id || `cs-${i}`,
      title: cs.title,
      sections: (cs.sections || []).map((s) => ({ label: s.label, text: s.text }))
    }));
  }
  return (l.materials || []).map((m, i) => ({
    id: m._id || `cs-${i}`,
    title: m.title,
    sections: BLANK_CASE_STUDY_SECTIONS.map((s) => ({ ...s }))
  }));
}

/** Empty state for a task-mode section that was never generated. */
const GenerateEmptyState: React.FC<{ description: string; loading: boolean; onGenerate: () => void }> = ({
  description,
  loading,
  onGenerate
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
    <Typography
      sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: '20px' }}
    >
      {description}
    </Typography>
    <ActionPill
      icon={<AutoAwesomeIcon sx={{ fontSize: 14, color: 'var(--Stroke-highlight, #F79420)' }} />}
      label={loading ? 'Generating...' : 'Generate with Elliott'}
      onClick={loading ? () => {} : onGenerate}
    />
  </Box>
);

// Shared "add" control for every learning section (case studies, quiz, materials)
// so adding sits and looks the same across all learning features. Rendered at the
// end of each section's list.
const SectionAddButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      mt: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.75,
      py: 1.25,
      borderRadius: '8px',
      border: '1px dashed var(--Stroke-Border-border-highlight, #868686)',
      cursor: 'pointer',
      color: 'var(--Text-text-secondary, #B6B6B6)',
      transition: 'border-color 0.15s ease, color 0.15s ease',
      '&:hover': { borderColor: 'var(--Stroke-highlight, #F79420)', color: 'var(--Stroke-highlight, #F79420)' }
    }}
  >
    <AddIcon sx={{ fontSize: 16 }} />
    <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: 'Inter' }}>{label}</Typography>
  </Box>
);

const LessonContentView: React.FC<LessonContentViewProps> = ({
  roleplayPolicy,
  headerAction,
  titleExtra,
  capabilityName,
  lesson,
  moduleName,
  moduleIndex,
  lessonIndex,
  generationId,
  sectionAdapter,
  onEdit,
  onImprove,
  onReasoningClick,
  reasoningDisabled,
  reasoningDisabledTooltip,
  onMaterialUpdate,
  onAssessmentsUpdate,
  onAddMaterial,
  onRemoveMaterial,
  resourcesSlot,
  videoSlot,
  onGenerateImage,
  onGenerateVideo,
  onRemoveMedia,
  imageCount = 0,
  videoCount = 0,
  isGeneratingImage = false,
  isGeneratingVideo = false
}) => {
  // Default card art for resources without their own cover: the org logo.
  const orgLogo = useAppSelector((s) => s.orgDetails.data?.orgLogo);
  // The capability the lesson builds: as given, or from the build flow's tags (the wizard's Content step).
  const lessonTags = useLessonTags();
  const lessonCapabilityName =
    capabilityName || (lessonTags && lesson.capabilityId ? lessonCapability(lesson, lessonTags)?.name : null) || null;
  const [previewMat, setPreviewMat] = useState<{ mat: Material; idx: number } | null>(null);
  const [editMat, setEditMat] = useState<{ mat: Material; idx: number } | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [expandedCaseStudy, setExpandedCaseStudy] = useState<number | null>(0);
  // Editable case studies — real data from lesson.elliott, refreshed after section improves.
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>(() => buildCaseStudies(lesson));
  useEffect(() => {
    setCaseStudies(buildCaseStudies(lesson));
  }, [lesson.materials, lesson.elliott]);
  // True while the per-lesson Elliott cascade hasn't delivered real case
  // studies yet — the list below is material-derived placeholder shells.
  const elliottCaseStudiesPending = !(lesson.elliott?.caseStudies && lesson.elliott.caseStudies.length > 0);
  const [csEdit, setCsEdit] = useState<{
    mode: 'edit' | 'add';
    index: number;
    title: string;
    sections: { label: string; text: string }[];
  } | null>(null);
  // Simulation rubric — lesson data until a section improve returns a fresh one.
  const [simRubricOverride, setSimRubricOverride] = useState<ElliottSimulationRubric | null>(null);
  // Section on/off toggles — local overrides until the lesson data refreshes (absent flag = on).
  const [simEnabledOverride, setSimEnabledOverride] = useState<boolean | null>(null);
  const [csEnabledOverride, setCsEnabledOverride] = useState<boolean | null>(null);
  const [quizEnabledOverride, setQuizEnabledOverride] = useState<boolean | null>(null);
  // The Roleplay was removed here (a lesson whose capability changed), until the lesson data refreshes.
  const [simRubricRemoved, setSimRubricRemoved] = useState(false);
  useEffect(() => {
    setSimRubricOverride(null);
    setSimRubricRemoved(false);
    setSimEnabledOverride(null);
    setCsEnabledOverride(null);
    setQuizEnabledOverride(null);
  }, [lesson]);
  const simRubric = simRubricRemoved ? null : simRubricOverride ?? lesson.elliott?.simulationRubric ?? null;
  const simEnabled = simEnabledOverride ?? lesson.elliott?.simulationEnabled ?? true;
  const csEnabled = csEnabledOverride ?? lesson.elliott?.caseStudiesEnabled ?? true;
  const quizEnabled = quizEnabledOverride ?? lesson.elliott?.quizEnabled ?? true;
  // Section-scoped "Improve with AI" — shared modal (no "regenerate all lessons" banner),
  // wired to POST /program-generation/{id}/improve-elliott-section. A case-study
  // target carries the card's index so only that one card is rewritten.
  const [sectionImproveTarget, setSectionImproveTarget] = useState<{
    section: ElliottSectionKey;
    caseStudyIndex?: number;
  } | null>(null);
  const [sectionImproveLoading, setSectionImproveLoading] = useState(false);
  // Task mode (live template) shows "Generate with AI" empty states; artifact mode
  // (the wizard) keeps the background-cascade spinners.
  const taskMode = sectionAdapter?.mode === 'task';
  // Relaunch editing freeze (B4-2): section edits here persist immediately, so
  // every write path bails with the freeze message before touching the network.
  const editingFrozen = useEditingFrozen();
  const [generatingSection, setGeneratingSection] = useState<ElliottSectionKey | null>(null);

  // Manual section edits persist through PATCH /program-generation/{id}/elliott-section
  // (artifact + live template mirror). Rubric typing is debounced.
  const persistSection = useCallback(
    async (payload: {
      caseStudies?: CaseStudy[];
      simulationRubric?: { brief: string; skill: string; strongPoints: string[]; weakPoints: string[] } | null;
      simulationEnabled?: boolean;
      caseStudiesEnabled?: boolean;
      quizEnabled?: boolean;
    }): Promise<boolean> => {
      if (editingFrozen) {
        toast.warn(EDITING_FROZEN_MESSAGE);
        return false;
      }
      // A refusal with a reason (a Roleplay switched on in a lesson that builds no capability, 409) says why.
      const failed = (err: any) =>
        toast.error(err?.response?.data?.message || 'Your section changes could not be saved. Try again.');
      if (sectionAdapter) {
        try {
          await sectionAdapter.persistSection(payload);
          return true;
        } catch (err) {
          failed(err);
          return false;
        }
      }
      if (!generationId) return false;
      try {
        await API.updateElliottSection({ generationId, moduleIndex, lessonIndex, ...payload });
        return true;
      } catch (err) {
        failed(err);
        return false;
      }
    },
    [editingFrozen, sectionAdapter, generationId, moduleIndex, lessonIndex]
  );

  const handleDeleteCaseStudy = useCallback(
    (idx: number) => {
      if (editingFrozen) {
        toast.warn(EDITING_FROZEN_MESSAGE);
        return;
      }
      const next = caseStudies.filter((_, i) => i !== idx);
      setCaseStudies(next);
      persistSection({ caseStudies: next });
      setExpandedCaseStudy((cur) => (cur === idx ? null : cur !== null && cur > idx ? cur - 1 : cur));
    },
    [editingFrozen, caseStudies, persistSection]
  );

  const rubricSaveTimer = useRef<number | null>(null);
  const pendingRubricRef = useRef<{
    brief: string;
    skill: string;
    strongPoints: string[];
    weakPoints: string[];
  } | null>(null);
  const handleRubricChange = (rubric: {
    brief: string;
    skill: string;
    strongPoints: string[];
    weakPoints: string[];
  }) => {
    if (editingFrozen) return; // nothing queued, nothing flushed on unmount
    pendingRubricRef.current = rubric;
    if (rubricSaveTimer.current) window.clearTimeout(rubricSaveTimer.current);
    rubricSaveTimer.current = window.setTimeout(() => {
      const pending = pendingRubricRef.current;
      pendingRubricRef.current = null;
      if (pending) persistSection({ simulationRubric: pending });
    }, 800);
  };
  // Flush a pending rubric save when the lesson changes or the view unmounts.
  useEffect(
    () => () => {
      if (rubricSaveTimer.current) window.clearTimeout(rubricSaveTimer.current);
      const pending = pendingRubricRef.current;
      pendingRubricRef.current = null;
      if (pending) persistSection({ simulationRubric: pending });
    },
    [persistSection]
  );

  // ----- Assessment state -----
  const [mcqs, setMcqs] = useState<MCQ[]>(() => buildMcqList(lesson));
  const [addingMcq, setAddingMcq] = useState(false);
  const [editMcqIdx, setEditMcqIdx] = useState<number | null>(null); // manual edit of an existing question
  const [newMcq, setNewMcq] = useState<MCQ>({ ...EMPTY_MCQ });
  const mcqFormOpen = addingMcq || editMcqIdx !== null;
  const closeMcqForm = () => {
    setAddingMcq(false);
    setEditMcqIdx(null);
    setNewMcq({ ...EMPTY_MCQ });
  };

  useEffect(() => {
    setMcqs(buildMcqList(lesson));
  }, [lesson.mcq, lesson.mcqs]);

  const persistMcqs = useCallback(
    (updated: MCQ[]) => {
      setMcqs(updated);
      onAssessmentsUpdate?.(updated);
    },
    [onAssessmentsUpdate]
  );

  // Apply fresh section data returned by an improve/generate to local state.
  const applyElliottSection = (
    section: ElliottSectionKey,
    elliott: Partial<ElliottSections> | undefined,
    freshMcqs?: MCQ[]
  ) => {
    if (section === 'caseStudies' && elliott?.caseStudies?.length) {
      setCaseStudies(
        elliott.caseStudies.map((cs, i) => ({
          id: cs.id || `cs-${i}`,
          title: cs.title,
          sections: (cs.sections || []).map((s) => ({ label: s.label, text: s.text }))
        }))
      );
    }
    if (section === 'simulation' && elliott?.simulationRubric) {
      setSimRubricOverride(elliott.simulationRubric);
    }
    if (section === 'quiz') {
      if (freshMcqs?.length) {
        setMcqs(freshMcqs);
      } else if (elliott?.quiz?.length) {
        setMcqs(
          elliott.quiz.map((q) => ({
            question: q.question,
            options: q.options ?? [],
            correct_answer: q.correct_answer || '',
            explanation: q.explanation
          }))
        );
      }
    }
  };

  // Section-scoped generate/improve. Routes through the task adapter when present
  // (live template), else the artifact endpoint. Empty feedback = plain generate.
  // `caseStudyIndex` narrows a caseStudies improve to that single card — the BE
  // splices the rewrite back in and returns the full (mostly unchanged) list.
  const runSectionImprove = async (
    section: ElliottSectionKey,
    feedback: string,
    caseStudyIndex?: number
  ): Promise<boolean> => {
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return false;
    }
    if (sectionAdapter) {
      if (!sectionAdapter.ready) {
        toast.error("This lesson isn't saved yet. Click Save Changes, then try again.");
        return false;
      }
      try {
        const res = await sectionAdapter.improveSection(section, feedback, caseStudyIndex);
        applyElliottSection(section, res?.elliott, res?.mcqs);
        toast.success(
          feedback ? (caseStudyIndex !== undefined ? 'Case study improved' : 'Section improved') : 'Section generated'
        );
        return true;
      } catch {
        toast.error('Elliott could not write this section. Try again.');
        return false;
      }
    }
    if (!generationId) {
      toast.error('Elliott can only improve sections in a program Elliott built.');
      return false;
    }
    try {
      const res = await API.improveElliottSection({
        generationId,
        moduleIndex,
        lessonIndex,
        section,
        feedback,
        caseStudyIndex
      });
      // The API wrapper nests the payload under `data` on some responses.
      applyElliottSection(section, res.data?.data?.elliott ?? res.data?.elliott);
      toast.success(caseStudyIndex !== undefined ? 'Case study improved' : 'Section improved');
      return true;
    } catch {
      toast.error('Elliott could not improve this section. Try again.');
      return false;
    }
  };

  const handleSectionImprove = async (feedback: string) => {
    if (!sectionImproveTarget) return;
    setSectionImproveLoading(true);
    const ok = await runSectionImprove(sectionImproveTarget.section, feedback, sectionImproveTarget.caseStudyIndex);
    setSectionImproveLoading(false);
    if (ok) setSectionImproveTarget(null);
  };

  const handleGenerateSection = async (section: ElliottSectionKey) => {
    setGeneratingSection(section);
    await runSectionImprove(section, '');
    setGeneratingSection(null);
  };

  // ----- The build flow's Roleplay rules (docs/PRD-capability-lesson-roleplays.md R9, D1, D2) -----
  const [roleplayConfirm, setRoleplayConfirm] = useState<CapabilityConfirmRequest | null>(null);
  const roleplayCapability = roleplayPolicy?.capability ?? null;

  /** Switches the Roleplay on or off and saves it; the editor's Scored Roleplay flag follows. */
  const setRoleplay = async (enabled: boolean): Promise<boolean> => {
    setSimEnabledOverride(enabled);
    if (!(await persistSection({ simulationEnabled: enabled }))) {
      setSimEnabledOverride(!enabled);
      return false;
    }
    roleplayPolicy?.onScoredChange(enabled);
    return true;
  };

  const handleSimToggle = async (enabled: boolean) => {
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return;
    }
    if (!roleplayPolicy) {
      setSimEnabledOverride(enabled);
      persistSection({ simulationEnabled: enabled });
      return;
    }
    if (enabled) {
      // D1: a Roleplay measures a capability, so a lesson that builds none is made to build one first,
      // and the Roleplay is written for it there and then.
      const capability = roleplayCapability ?? (await roleplayPolicy.pickCapability('switchOn'));
      if (!capability) return;
      if (!(await setRoleplay(true))) return;
      if (!simRubric?.brief) await handleGenerateSection('simulation');
      return;
    }
    const name = roleplayCapability?.name ?? '';
    if (roleplayPolicy.lastScored) {
      setRoleplayConfirm({
        title: `Turn off ${name}'s last Scored Roleplay?`,
        body: `This course won't measure ${name} any more. Learners can still be measured by an Assessment or a Scored Roleplay of their own.`,
        confirmLabel: 'Turn off',
        onConfirm: () => {
          setRoleplay(false);
        }
      });
      return;
    }
    if (await setRoleplay(false)) {
      const where = roleplayPolicy.stillMeasuredIn;
      toastWithUndo(where ? `Roleplay off. ${name} is still measured in "${where}".` : 'Roleplay off.', () => {
        setRoleplay(true);
      });
    }
  };

  /** Deletes the Roleplay: one written for a capability the lesson no longer builds can't stay (R9). */
  const removeRoleplay = async () => {
    if (!(await persistSection({ simulationRubric: null, simulationEnabled: false }))) return;
    setSimRubricRemoved(true);
    setSimEnabledOverride(false);
    roleplayPolicy?.onScoredChange(false);
  };

  const handleChangeCapability = async () => {
    if (!roleplayPolicy) return;
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return;
    }
    const before = roleplayCapability;
    const hadRoleplay = Boolean(simRubric?.brief);
    const next = await roleplayPolicy.pickCapability('change');
    if (!next || next.id === before?.id || !hadRoleplay) return;
    setRoleplayConfirm({
      title: `Rewrite this Roleplay for ${next.name}?`,
      body: `This lesson's Roleplay was written for ${
        before?.name ?? 'another capability'
      }'s levels, so it can't stay as it is. Regenerate it for ${next.name}, or remove it.`,
      confirmLabel: `Regenerate for ${next.name}`,
      onConfirm: () => {
        handleGenerateSection('simulation');
      },
      alt: {
        label: 'Remove Roleplay',
        onClick: () => {
          removeRoleplay();
        }
      },
      required: true
    });
  };

  const handleMcqDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination, type } = result;
      if (!destination) return;
      if (source.droppableId === destination.droppableId && source.index === destination.index) return;

      if (type === 'ASSESSMENT') {
        const updated = [...mcqs];
        const [moved] = updated.splice(source.index, 1);
        updated.splice(destination.index, 0, moved);
        persistMcqs(updated);
        return;
      }

      if (type === 'OPTION') {
        const mcqIdx = parseInt(source.droppableId.replace('opts-', ''), 10);
        const updated = mcqs.map((m, i) => {
          if (i !== mcqIdx) return m;
          const opts = [...m.options];
          const [moved] = opts.splice(source.index, 1);
          opts.splice(destination.index, 0, moved);
          // Keep per-option reasonings aligned with their option when reordered.
          const reasonings = m.options.map((_, oi) => m.optionReasonings?.[oi] || '');
          const [movedR] = reasonings.splice(source.index, 1);
          reasonings.splice(destination.index, 0, movedR);
          const oldCorrectIdx = getCorrectIndex(m.options, m.correct_answer);
          let newCorrectIdx = oldCorrectIdx;
          if (oldCorrectIdx >= 0) {
            if (oldCorrectIdx === source.index) newCorrectIdx = destination.index;
            else if (source.index < oldCorrectIdx && destination.index >= oldCorrectIdx) newCorrectIdx--;
            else if (source.index > oldCorrectIdx && destination.index <= oldCorrectIdx) newCorrectIdx++;
          }
          return { ...m, options: opts, correct_answer: String(newCorrectIdx), optionReasonings: reasonings };
        });
        persistMcqs(updated);
      }
    },
    [mcqs, persistMcqs]
  );

  const handleSaveMcq = useCallback(() => {
    if (!newMcq.question.trim()) return;
    if (editMcqIdx !== null) {
      persistMcqs(mcqs.map((m, i) => (i === editMcqIdx ? { ...newMcq } : m)));
    } else {
      persistMcqs([...mcqs, { ...newMcq }]);
    }
    setNewMcq({ ...EMPTY_MCQ });
    setAddingMcq(false);
    setEditMcqIdx(null);
  }, [mcqs, newMcq, editMcqIdx, persistMcqs]);

  const handleDeleteMcq = useCallback(
    (idx: number) => {
      persistMcqs(mcqs.filter((_, i) => i !== idx));
    },
    [mcqs, persistMcqs]
  );

  if (lesson.status === 'generating') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pb: 6 }}>
        <ElliottWorking size="full" message="Elliott is writing the Lesson Body..." />
        <Typography
          sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', textAlign: 'center' }}
        >
          You can open other lessons while Elliott writes this one.
        </Typography>
      </Box>
    );
  }

  if (lesson.status === 'pending') {
    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, py: 6 }}
      >
        <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}>
          The Lesson Body for this lesson is not written yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)', mb: 0.5 }}>
            {moduleName}
          </Typography>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
              <Typography
                sx={{ fontSize: 22, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}
              >
                {lesson.title}
              </Typography>
              {titleExtra}
              {lessonCapabilityName && <CapabilityChip name={lessonCapabilityName} />}
            </Box>
            {headerAction}
          </Box>
        </Box>
      </Box>

      {/* Lesson content — its CTAs live inside this box and act on the Content only */}
      <Box sx={sectionBoxSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.5,
            flexWrap: 'wrap'
          }}
        >
          <Typography
            sx={{ fontSize: 15, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}
          >
            Lesson Body
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {onReasoningClick && (
              <ActionPill
                icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
                label="Elliott's reasoning"
                onClick={onReasoningClick}
                disabled={reasoningDisabled}
                disabledTooltip={reasoningDisabledTooltip}
              />
            )}
            {onImprove && (
              <ActionPill
                icon={<AutoAwesomeIcon sx={{ fontSize: 14, color: 'var(--Stroke-highlight, #F79420)' }} />}
                label="Improve with Elliott"
                onClick={onImprove}
              />
            )}
            <ActionPill icon={<EditIcon sx={{ fontSize: 14 }} />} label="Edit" onClick={onEdit} />
            {onGenerateVideo && (
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <ActionPill
                  icon={<AutoAwesomeIcon sx={{ fontSize: 14, color: 'var(--Stroke-highlight, #F79420)' }} />}
                  label={
                    isGeneratingVideo
                      ? 'Generating Now'
                      : `Generate Lesson Video${videoCount > 0 ? ` (${3 - videoCount} left)` : ''}`
                  }
                  onClick={isGeneratingVideo ? () => {} : onGenerateVideo}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: 4,
                    background: 'var(--Stroke-highlight, #F79420)',
                    color: '#fff',
                    fontSize: 9,
                    fontWeight: 700,
                    fontFamily: 'Inter',
                    px: 0.6,
                    py: 0.2,
                    borderRadius: '4px',
                    lineHeight: '14px',
                    pointerEvents: 'none',
                    letterSpacing: '0.3px'
                  }}
                >
                  BETA
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          className="aimd-lesson-content"
          sx={{
            color: '#FDFDFD',
            fontFamily: 'Inter',
            fontSize: 14,
            lineHeight: '24px',
            '& *': { color: 'inherit' },
            '& p': { m: 0, mb: 1.5 },
            '& ul, & ol': { pl: 3, mb: 1.5 },
            '& li': { mb: 0.5 },
            '& h1': { fontSize: 24, fontWeight: 700, mb: 1.5 },
            '& h2': { fontSize: 20, fontWeight: 700, mb: 1.5 },
            '& h3': { fontSize: 18, fontWeight: 600, mb: 1 },
            '& h4': { fontSize: 16, fontWeight: 600, mb: 1 },
            '& strong, & b': { fontWeight: 700 },
            '& em, & i': { fontStyle: 'italic' },
            '& u': { textDecoration: 'underline' },
            '& a': { color: 'var(--Stroke-highlight, #F79420)', textDecoration: 'underline' },
            '& img': { maxWidth: '100%', height: 'auto', borderRadius: '8px', my: 2, display: 'block' },
            '& video': { maxWidth: '100%', borderRadius: '8px', my: 2 },
            '& iframe': { maxWidth: '100%', borderRadius: '8px', my: 2, border: 'none' },
            '& blockquote': {
              borderLeft: '3px solid var(--Stroke-highlight, #F79420)',
              pl: 2,
              ml: 0,
              my: 1.5,
              color: 'var(--Text-text-secondary, #B6B6B6)',
              fontStyle: 'italic'
            },
            '& pre, & code': {
              fontFamily: 'monospace',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '4px',
              px: 0.5
            },
            '& pre': { p: 2, overflowX: 'auto', my: 1.5 },
            '& table': { borderCollapse: 'collapse', width: '100%', my: 1.5 },
            '& th, & td': {
              border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
              p: 1,
              textAlign: 'left'
            },
            '& th': { background: 'rgba(255,255,255,0.05)', fontWeight: 600 }
          }}
        >
          {(() => {
            const parserOptions: HTMLReactParserOptions = {
              replace(node) {
                if (!(node instanceof Element)) return;
                if (node.name === 'img' || node.name === 'video') {
                  const src = node.attribs?.src || '';
                  if (!src) return;
                  const type = node.name as 'img' | 'video';
                  return (
                    <MediaWrapper
                      key={src}
                      type={type}
                      src={src}
                      attribs={node.attribs || {}}
                      onRemove={() => onRemoveMedia?.(src, type)}
                    />
                  );
                }
              }
            };
            return parse(normalizeContentToHtml(lesson.content), parserOptions);
          })()}
        </Box>
      </Box>

      {/* Lesson Video — the AI video builder card (design editor, flag-gated). */}
      {videoSlot}

      {/* Lesson Resources — directly below the Lesson Content. Either the parent's
          fully-managed section (design editor) or the built-in list below (wizard). */}
      {resourcesSlot}
      {!resourcesSlot && onAddMaterial && (
        <Box sx={sectionBoxSx}>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 600,
              fontFamily: 'Inter',
              color: 'var(--Text-text-tertiary, #868686)',
              mb: 1.5
            }}
          >
            Resources
          </Typography>
          {(lesson.materials || []).length === 0 ? (
            <Typography
              sx={{
                fontSize: 12,
                fontFamily: 'Inter',
                color: 'var(--Text-text-secondary, #B6B6B6)',
                fontStyle: 'italic'
              }}
            >
              No resources for this lesson yet.
            </Typography>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={2}>
              {(lesson.materials || []).map((mat, idx) => (
                <Box
                  key={mat._id || idx}
                  onClick={() => setPreviewMat({ mat, idx })}
                  sx={{ width: 150, display: 'flex', flexDirection: 'column', gap: 1, cursor: 'pointer' }}
                >
                  <Box
                    component="img"
                    src={mat.thumbnail || orgLogo || dosenFavicon}
                    alt={mat.title || 'Resource'}
                    onError={(e: any) => {
                      e.target.src = orgLogo || dosenFavicon;
                    }}
                    sx={{
                      width: '100%',
                      height: 90,
                      borderRadius: 1,
                      outline: '2px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                      objectFit: 'cover'
                    }}
                  />
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 12,
                        fontWeight: 500,
                        fontFamily: 'Inter',
                        color: 'var(--Text-text-primary, #FDFDFD)',
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      {mat.title || 'Resource'}
                    </Typography>
                    {onRemoveMaterial && (
                      <DeleteOutlineIcon
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onRemoveMaterial(idx);
                        }}
                        sx={{
                          fontSize: 16,
                          color: 'var(--Text-text-tertiary, #868686)',
                          cursor: 'pointer',
                          flexShrink: 0,
                          '&:hover': { color: 'var(--UI-Error, #F44336)' }
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          <SectionAddButton label="Add resource" onClick={onAddMaterial} />
        </Box>
      )}

      {/* Case Studies — learner-style accordion cards, with Improve-with-AI + manual edit */}
      <Box sx={sectionBoxSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.5,
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{ fontSize: 15, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}
            >
              Case Studies
            </Typography>
            {csEnabled && elliottCaseStudiesPending && !taskMode && (
              <>
                <CircularProgress size={12} sx={{ color: 'var(--Stroke-highlight, #F79420)' }} />
                <Typography sx={{ fontSize: 12, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}>
                  Elliott is writing the case studies...
                </Typography>
              </>
            )}
          </Box>
          <SectionToggle
            enabled={csEnabled}
            onToggle={(enabled) => {
              if (editingFrozen) {
                toast.warn(EDITING_FROZEN_MESSAGE);
                return;
              }
              setCsEnabledOverride(enabled);
              persistSection({ caseStudiesEnabled: enabled });
            }}
            title="Turn the Case Studies step on or off for this lesson"
          />
        </Box>
        {!csEnabled && (
          <Typography
            sx={{
              fontSize: 12,
              fontFamily: 'Inter',
              color: 'var(--Text-text-secondary, #B6B6B6)',
              lineHeight: 1.5,
              fontStyle: 'italic'
            }}
          >
            Case Studies are turned off for this lesson. Learners will move straight from the Lesson Body to the next
            next step. Turn them back on to restore this step (your case studies are kept).
          </Typography>
        )}
        {csEnabled && taskMode && caseStudies.length === 0 && (
          <GenerateEmptyState
            description="Elliott hasn't written case studies for this lesson yet. Generate case studies grounded in the Lesson Body, or add one manually below."
            loading={generatingSection === 'caseStudies'}
            onGenerate={() => handleGenerateSection('caseStudies')}
          />
        )}
        {csEnabled && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              mt: taskMode && caseStudies.length === 0 ? 1.5 : 0
            }}
          >
            {caseStudies.map((cs, i) => {
              const open = expandedCaseStudy === i;
              return (
                <Box
                  key={cs.id}
                  sx={{
                    borderRadius: '8px',
                    px: 2,
                    py: 1.5,
                    background: 'linear-gradient(180deg, rgba(247,148,32,0.1) 0%, rgba(241,90,38,0.1) 100%)'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box
                      onClick={() => setExpandedCaseStudy(open ? null : i)}
                      sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, cursor: 'pointer' }}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          fontFamily: 'Inter',
                          color: 'var(--Text-text-primary, #FDFDFD)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {cs.title}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      <ActionPill
                        icon={<AutoAwesomeIcon sx={{ fontSize: 14, color: 'var(--Stroke-highlight, #F79420)' }} />}
                        label="Improve with Elliott"
                        onClick={() => setSectionImproveTarget({ section: 'caseStudies', caseStudyIndex: i })}
                      />
                      <ActionPill
                        icon={<EditIcon sx={{ fontSize: 14 }} />}
                        label="Edit"
                        onClick={() =>
                          setCsEdit({
                            mode: 'edit',
                            index: i,
                            title: cs.title,
                            sections: cs.sections.map((s) => ({ ...s }))
                          })
                        }
                      />
                      <DeleteOutlineIcon
                        onClick={() => handleDeleteCaseStudy(i)}
                        sx={{
                          fontSize: 16,
                          color: 'var(--Text-text-tertiary, #868686)',
                          cursor: 'pointer',
                          '&:hover': { color: 'var(--UI-Error, #F44336)' }
                        }}
                      />
                      <Box
                        onClick={() => setExpandedCaseStudy(open ? null : i)}
                        sx={{ display: 'flex', cursor: 'pointer', color: 'var(--Text-text-secondary, #B6B6B6)' }}
                      >
                        {open ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                      </Box>
                    </Box>
                  </Box>
                  {open && (
                    <Box
                      sx={{
                        mt: 1,
                        pt: 1,
                        borderTop: '1px solid rgba(255,255,255,0.45)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5
                      }}
                    >
                      {elliottCaseStudiesPending && cs.sections.every((s) => !s.text.trim()) ? (
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontFamily: 'Inter',
                            fontStyle: 'italic',
                            color: 'var(--Text-text-secondary, #B6B6B6)',
                            lineHeight: '24px'
                          }}
                        >
                          Elliott is still writing this case study. The Scenario, Action and Relevance will appear here
                          shortly.
                        </Typography>
                      ) : (
                        cs.sections.map((s) => (
                          <Typography
                            key={s.label}
                            sx={{
                              fontSize: 14,
                              fontFamily: 'Inter',
                              color: 'var(--Text-text-primary, #FDFDFD)',
                              lineHeight: '24px',
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              {s.label}:
                            </Box>{' '}
                            {s.text}
                          </Typography>
                        ))
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
            <SectionAddButton
              label="Add case study"
              onClick={() =>
                setCsEdit({
                  mode: 'add',
                  index: caseStudies.length,
                  title: '',
                  sections: BLANK_CASE_STUDY_SECTIONS.map((s) => ({ ...s }))
                })
              }
            />
          </Box>
        )}
      </Box>

      {/* Live Simulation */}
      <Box sx={sectionBoxSx}>
        {roleplayPolicy && (
          // The build flow (R9): what the lesson builds, Change, and the Scored Roleplay chip.
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}>
              {roleplayCapability
                ? `Builds ${roleplayCapability.name}`
                : roleplayPolicy.outcomes.length > 0
                ? `Serves ${roleplayPolicy.outcomes.join(', ')}`
                : 'Builds no capability'}
            </Typography>
            {roleplayCapability && (
              <Box
                component="button"
                type="button"
                onClick={() => {
                  handleChangeCapability();
                }}
                sx={{
                  background: 'none',
                  border: 'none',
                  p: 0,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontFamily: 'Inter',
                  color: 'var(--Stroke-highlight, #F79420)'
                }}
              >
                Change
              </Box>
            )}
            {roleplayCapability && simEnabled && (
              <Box
                data-testid="scored-roleplay-chip"
                sx={{
                  ml: 'auto',
                  px: 1,
                  py: 0.25,
                  borderRadius: '4px',
                  fontSize: 11,
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  color: 'var(--Stroke-highlight, #F79420)',
                  border: '1px solid var(--Stroke-highlight, #F79420)'
                }}
              >
                Scored Roleplay · {roleplayCapability.name}
              </Box>
            )}
          </Box>
        )}
        {(taskMode || (roleplayPolicy && lesson.elliott)) && !simRubric ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap'
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 240 }}>
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 600,
                    fontFamily: 'Inter',
                    color: 'var(--Text-text-tertiary, #868686)'
                  }}
                >
                  Roleplay
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontFamily: 'Inter',
                    color: 'var(--Text-text-secondary, #B6B6B6)',
                    lineHeight: 1.5
                  }}
                >
                  Roleplay presents a realistic situation the learner works through with Elliott, verifying they can
                  apply the lesson, not just recall it.
                </Typography>
              </Box>
              <SectionToggle
                enabled={simEnabled}
                onToggle={(enabled) => {
                  handleSimToggle(enabled);
                }}
                title="Turn the Scenario Testing step on or off for this lesson"
                label="Roleplay"
              />
            </Box>
            {simEnabled ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                <GenerateEmptyState
                  description={
                    roleplayCapability
                      ? generatingSection === 'simulation'
                        ? `Elliott is writing a Roleplay for ${roleplayCapability.name}'s four levels…`
                        : `This lesson has no Roleplay yet. Elliott writes it for ${roleplayCapability.name}'s four levels, and it counts toward each learner's level.`
                      : 'This lesson has no Roleplay yet. Generate a Roleplay brief and scoring rubric (strong / weak response points) from the Lesson Body.'
                  }
                  loading={generatingSection === 'simulation'}
                  onGenerate={() => handleGenerateSection('simulation')}
                />
                <Box
                  onClick={() =>
                    setSimRubricOverride({ brief: '', skill: '', strongPoints: [], weakPoints: [], maxAttempts: 5 })
                  }
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    color: 'var(--Text-text-tertiary, #868686)',
                    '&:hover': { color: 'var(--Stroke-highlight, #F79420)' }
                  }}
                >
                  <EditIcon sx={{ fontSize: 13 }} />
                  <Typography sx={{ fontSize: 12, fontFamily: 'Inter' }}>...or write it manually</Typography>
                </Box>
              </Box>
            ) : (
              <Typography
                sx={{
                  fontSize: 12,
                  fontFamily: 'Inter',
                  color: 'var(--Text-text-secondary, #B6B6B6)',
                  lineHeight: 1.5,
                  fontStyle: 'italic'
                }}
              >
                {roleplayPolicy
                  ? roleplayCapability
                    ? `Off for this lesson. Turn it on to add a Scored Roleplay for ${roleplayCapability.name}. Elliott writes it for ${roleplayCapability.name}'s four levels, and it counts toward each learner's level.`
                    : 'Off for this lesson. Roleplays in this course measure a capability, so turning it on asks which capability this lesson builds.'
                  : 'Roleplay is turned off for this lesson. Learners will move straight from the case studies to the quiz.'}
              </Typography>
            )}
          </Box>
        ) : (
          <LiveSimulationSection
            rubric={simRubric}
            onReasoning={onReasoningClick}
            onImprove={() => setSectionImproveTarget({ section: 'simulation' })}
            onChange={handleRubricChange}
            enabled={simEnabled}
            onToggleEnabled={(enabled) => {
              handleSimToggle(enabled);
            }}
          />
        )}
        <CapabilityConfirm request={roleplayConfirm} onClose={() => setRoleplayConfirm(null)} />
      </Box>

      {/* Lesson Quiz */}
      <Box sx={sectionBoxSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.5,
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{ fontSize: 15, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}
            >
              Quiz
            </Typography>
          </Box>
          <SectionToggle
            enabled={quizEnabled}
            onToggle={(enabled) => {
              if (editingFrozen) {
                toast.warn(EDITING_FROZEN_MESSAGE);
                return;
              }
              setQuizEnabledOverride(enabled);
              persistSection({ quizEnabled: enabled });
            }}
            title="Turn the Quiz step on or off for this lesson"
          />
        </Box>
        {!quizEnabled && (
          <Typography
            sx={{
              fontSize: 12,
              fontFamily: 'Inter',
              color: 'var(--Text-text-secondary, #B6B6B6)',
              lineHeight: 1.5,
              fontStyle: 'italic'
            }}
          >
            The quiz is turned off for this lesson, so learners finish the lesson without a quiz. Your questions are
            saved and return when you turn the quiz back on.
          </Typography>
        )}
        {quizEnabled && taskMode && mcqs.length === 0 && (
          <Box sx={{ mb: 1.5 }}>
            <GenerateEmptyState
              description="No quiz questions yet. Generate a short multiple-choice quiz from the Lesson Body, or add one yourself with “Add question”."
              loading={generatingSection === 'quiz'}
              onGenerate={() => handleGenerateSection('quiz')}
            />
          </Box>
        )}
        {quizEnabled && (
          <>
            <DragDropContext onDragEnd={handleMcqDragEnd}>
              <Droppable droppableId="assessments" type="ASSESSMENT">
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    {mcqs.map((q, qIdx) => {
                      const cIdx = getCorrectIndex(q.options, q.correct_answer);
                      return (
                        <Draggable key={`mcq-${qIdx}`} draggableId={`mcq-${qIdx}`} index={qIdx}>
                          {(dragProv, dragSnap) => (
                            <Box
                              ref={dragProv.innerRef}
                              {...dragProv.draggableProps}
                              sx={{
                                background: '#1A1A1A',
                                border: `1px solid ${
                                  dragSnap.isDragging
                                    ? 'var(--Stroke-highlight, #F79420)'
                                    : 'var(--Stroke-Border-border-dim, #2A2A2A)'
                                }`,
                                borderRadius: '8px',
                                p: 2,
                                transition: 'border-color 0.15s'
                              }}
                            >
                              {/* MCQ header */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                <Box
                                  {...dragProv.dragHandleProps}
                                  sx={{ cursor: 'grab', flexShrink: 0, display: 'flex' }}
                                >
                                  <DragIndicatorIcon
                                    sx={{ fontSize: 16, color: 'var(--Text-text-tertiary, #868686)' }}
                                  />
                                </Box>
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    fontFamily: 'Inter',
                                    color: 'var(--Text-text-primary, #FDFDFD)',
                                    flex: 1,
                                    minWidth: 0
                                  }}
                                >
                                  {q.question || `Question ${qIdx + 1}`}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                  <ActionPill
                                    icon={
                                      <AutoAwesomeIcon
                                        sx={{ fontSize: 14, color: 'var(--Stroke-highlight, #F79420)' }}
                                      />
                                    }
                                    label="Improve with Elliott"
                                    onClick={() => setSectionImproveTarget({ section: 'quiz' })}
                                  />
                                  <ActionPill
                                    icon={<EditIcon sx={{ fontSize: 14 }} />}
                                    label="Edit"
                                    onClick={() => {
                                      setEditMcqIdx(qIdx);
                                      setNewMcq({ ...q, options: [...q.options] });
                                      setAddingMcq(false);
                                    }}
                                  />
                                  <DeleteOutlineIcon
                                    onClick={() => handleDeleteMcq(qIdx)}
                                    sx={{
                                      fontSize: 16,
                                      color: 'var(--Text-text-tertiary, #868686)',
                                      cursor: 'pointer',
                                      '&:hover': { color: 'var(--UI-Error, #F44336)' }
                                    }}
                                  />
                                </Box>
                              </Box>

                              {/* Options with DnD */}
                              <Droppable droppableId={`opts-${qIdx}`} type="OPTION">
                                {(optProv) => (
                                  <Box ref={optProv.innerRef} {...optProv.droppableProps}>
                                    {q.options.map((opt, oIdx) => {
                                      const isCorrect = oIdx === cIdx;
                                      return (
                                        <Draggable
                                          key={`opt-${qIdx}-${oIdx}`}
                                          draggableId={`opt-${qIdx}-${oIdx}`}
                                          index={oIdx}
                                        >
                                          {(oProv) => (
                                            <Box
                                              ref={oProv.innerRef}
                                              {...oProv.draggableProps}
                                              sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.5 }}
                                            >
                                              <Box {...oProv.dragHandleProps} sx={{ cursor: 'grab', flexShrink: 0 }}>
                                                <DragIndicatorIcon
                                                  sx={{ fontSize: 12, color: 'var(--Text-text-tertiary, #868686)' }}
                                                />
                                              </Box>
                                              {isCorrect ? (
                                                <CheckCircleIcon
                                                  sx={{
                                                    fontSize: 18,
                                                    color: 'var(--UI-Success, #4CAF50)',
                                                    flexShrink: 0
                                                  }}
                                                />
                                              ) : (
                                                <Box
                                                  sx={{
                                                    width: 18,
                                                    height: 18,
                                                    borderRadius: '50%',
                                                    border: '1.5px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontSize: 9,
                                                      fontFamily: 'Inter',
                                                      color: 'var(--Text-text-tertiary, #868686)'
                                                    }}
                                                  >
                                                    {String.fromCharCode(65 + oIdx)}
                                                  </Typography>
                                                </Box>
                                              )}
                                              <Box
                                                sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}
                                              >
                                                <Typography
                                                  sx={{
                                                    fontSize: 12,
                                                    fontFamily: 'Inter',
                                                    color: isCorrect
                                                      ? 'var(--UI-Success, #4CAF50)'
                                                      : 'var(--Text-text-secondary, #B6B6B6)',
                                                    fontWeight: isCorrect ? 600 : 400
                                                  }}
                                                >
                                                  {opt}
                                                </Typography>
                                                {q.optionReasonings?.[oIdx] && (
                                                  <Typography
                                                    sx={{
                                                      fontSize: 11,
                                                      fontFamily: 'Inter',
                                                      lineHeight: '15px',
                                                      fontStyle: 'italic',
                                                      color: 'var(--Text-text-tertiary, #868686)'
                                                    }}
                                                  >
                                                    {q.optionReasonings[oIdx]}
                                                  </Typography>
                                                )}
                                              </Box>
                                            </Box>
                                          )}
                                        </Draggable>
                                      );
                                    })}
                                    {optProv.placeholder}
                                  </Box>
                                )}
                              </Droppable>
                            </Box>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>

            {/* One consistent add control (matches case studies / materials). Opens
                an empty question the admin can fill manually or have Elliott write. */}
            {!mcqFormOpen && (
              <SectionAddButton
                label="Add question"
                onClick={() => {
                  setEditMcqIdx(null);
                  setAddingMcq(true);
                  setNewMcq({ ...EMPTY_MCQ });
                }}
              />
            )}

            {/* Add / Edit MCQ Form */}
            {mcqFormOpen && (
              <Box
                sx={{
                  mt: 2,
                  background: '#1A1A1A',
                  border: '1px solid var(--Stroke-highlight, #F79420)',
                  borderRadius: '8px',
                  p: 2.5
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 2,
                    flexWrap: 'wrap'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: 'Inter',
                      color: 'var(--Text-text-primary, #FDFDFD)'
                    }}
                  >
                    {editMcqIdx !== null ? 'Edit question' : 'Add question'}
                  </Typography>
                  {/* Write it yourself below, or let Elliott draft it. There is no
                      single-question generate endpoint yet, so this opens the quiz
                      Improve-with-AI flow (describe what you want), which regenerates
                      the WHOLE quiz — the modal says so when questions already exist.
                      Swap to a true single-question append once the BE endpoint lands. */}
                  {editMcqIdx === null && (
                    <ActionPill
                      icon={<AutoAwesomeIcon sx={{ fontSize: 14, color: 'var(--Stroke-highlight, #F79420)' }} />}
                      label="Generate with Elliott"
                      onClick={() => {
                        closeMcqForm();
                        setSectionImproveTarget({ section: 'quiz' });
                      }}
                    />
                  )}
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 12, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', mb: 0.5 }}
                  >
                    Question
                  </Typography>
                  <Box
                    component="input"
                    value={newMcq.question}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setNewMcq((p) => ({ ...p, question: e.target.value }))
                    }
                    placeholder="Type your question..."
                    sx={{
                      width: '100%',
                      background: '#0F0F0F',
                      border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                      borderRadius: '6px',
                      px: 1.5,
                      py: 1,
                      color: 'var(--Text-text-primary, #FDFDFD)',
                      fontFamily: 'Inter',
                      fontSize: 13,
                      outline: 'none',
                      '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                    }}
                  />
                </Box>

                <Typography
                  sx={{ fontSize: 12, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', mb: 1 }}
                >
                  Options (select the correct answer)
                </Typography>
                {newMcq.options.map((opt, oIdx) => (
                  <Box key={oIdx} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Radio
                        size="small"
                        checked={newMcq.correct_answer === String(oIdx)}
                        onChange={() => setNewMcq((p) => ({ ...p, correct_answer: String(oIdx) }))}
                        sx={{
                          p: 0.5,
                          color: 'var(--Stroke-Border-border-dim, #2A2A2A)',
                          '&.Mui-checked': { color: 'var(--UI-Success, #4CAF50)' }
                        }}
                      />
                      <Box
                        component="input"
                        value={opt}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const opts = [...newMcq.options];
                          opts[oIdx] = e.target.value;
                          setNewMcq((p) => ({ ...p, options: opts }));
                        }}
                        placeholder={`Option ${String.fromCharCode(65 + oIdx)}`}
                        sx={{
                          flex: 1,
                          background: '#0F0F0F',
                          border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                          borderRadius: '6px',
                          px: 1.5,
                          py: 0.75,
                          color: 'var(--Text-text-primary, #FDFDFD)',
                          fontFamily: 'Inter',
                          fontSize: 13,
                          outline: 'none',
                          '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                        }}
                      />
                    </Box>
                    {/* Per-option reasoning (shown to the learner after they answer). */}
                    <Box
                      component="textarea"
                      value={newMcq.optionReasonings?.[oIdx] || ''}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        const rs = newMcq.options.map((_, i) =>
                          i === oIdx ? e.target.value : newMcq.optionReasonings?.[i] || ''
                        );
                        setNewMcq((p) => ({ ...p, optionReasonings: rs }));
                      }}
                      rows={2}
                      placeholder={`Why ${String.fromCharCode(65 + oIdx)} is ${
                        newMcq.correct_answer === String(oIdx) ? 'correct' : 'incorrect'
                      } (shown after the learner answers)`}
                      sx={{
                        width: 'calc(100% - 38px)',
                        ml: '38px',
                        mt: 0.5,
                        resize: 'vertical',
                        background: 'transparent',
                        border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                        borderRadius: '6px',
                        px: 1.25,
                        py: 0.75,
                        color: 'var(--Text-text-secondary, #B6B6B6)',
                        fontFamily: 'Inter',
                        fontSize: 12,
                        lineHeight: '16px',
                        outline: 'none',
                        '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                      }}
                    />
                  </Box>
                ))}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
                  <Box component="button" onClick={closeMcqForm} sx={{ ...secondaryButtonSx, cursor: 'pointer' }}>
                    Cancel
                  </Box>
                  <Box
                    component="button"
                    onClick={handleSaveMcq}
                    disabled={!newMcq.question.trim()}
                    sx={{
                      ...ctaButtonSx,
                      border: 'none',
                      cursor: 'pointer',
                      '&:disabled': { ...buttonDisabledSx, cursor: 'default' }
                    }}
                  >
                    {editMcqIdx !== null ? 'Save Question' : 'Add Question'}
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Material Preview Modal */}
      <Dialog
        open={!!previewMat}
        onClose={() => setPreviewMat(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1A1A1A',
            borderRadius: '12px',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            p: 3
          }
        }}
      >
        {previewMat && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography
                sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}
              >
                {previewMat.mat.title}
              </Typography>
              <Box
                component="button"
                onClick={() => setPreviewMat(null)}
                sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0.5, flexShrink: 0 }}
              >
                <CloseIcon sx={{ color: 'var(--Text-text-secondary, #B6B6B6)', fontSize: 22 }} />
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: 14,
                fontFamily: 'Inter',
                color: 'var(--Text-text-secondary, #B6B6B6)',
                lineHeight: '24px'
              }}
            >
              {previewMat.mat.description}
            </Typography>
            {onMaterialUpdate && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Box
                  component="button"
                  onClick={() => {
                    setEditTitle(previewMat.mat.title);
                    setEditDesc(previewMat.mat.description);
                    setEditMat(previewMat);
                    setPreviewMat(null);
                  }}
                  sx={{ ...secondaryButtonSx, cursor: 'pointer' }}
                >
                  Edit
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Dialog>

      {/* Material Edit Modal */}
      <Dialog
        open={!!editMat}
        onClose={() => setEditMat(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1A1A1A',
            borderRadius: '12px',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            p: 3
          }
        }}
      >
        {editMat && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography
                sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}
              >
                Edit resource
              </Typography>
              <Box
                component="button"
                onClick={() => setEditMat(null)}
                sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0.5, flexShrink: 0 }}
              >
                <CloseIcon sx={{ color: 'var(--Text-text-secondary, #B6B6B6)', fontSize: 22 }} />
              </Box>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'Inter',
                  color: 'var(--Text-text-secondary, #B6B6B6)',
                  mb: 1
                }}
              >
                Title
              </Typography>
              <Box
                component="input"
                value={editTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditTitle(e.target.value)}
                sx={{
                  width: '100%',
                  background: 'transparent',
                  border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                  borderRadius: '8px',
                  px: 1.5,
                  py: 1,
                  color: 'var(--Text-text-primary, #FDFDFD)',
                  fontFamily: 'Inter',
                  fontSize: 14,
                  outline: 'none',
                  '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'Inter',
                  color: 'var(--Text-text-secondary, #B6B6B6)',
                  mb: 1
                }}
              >
                Description
              </Typography>
              <Box
                component="textarea"
                value={editDesc}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditDesc(e.target.value)}
                rows={5}
                sx={{
                  width: '100%',
                  background: 'transparent',
                  resize: 'vertical',
                  border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                  borderRadius: '8px',
                  px: 1.5,
                  py: 1,
                  color: 'var(--Text-text-primary, #FDFDFD)',
                  fontFamily: 'Inter',
                  fontSize: 14,
                  outline: 'none',
                  '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 1 }}>
              <Box component="button" onClick={() => setEditMat(null)} sx={{ ...secondaryButtonSx, cursor: 'pointer' }}>
                Cancel
              </Box>
              <Box
                component="button"
                onClick={() => {
                  if (onMaterialUpdate) {
                    onMaterialUpdate(editMat.idx, {
                      ...editMat.mat,
                      title: editTitle,
                      description: editDesc
                    });
                  }
                  setEditMat(null);
                }}
                sx={{ ...ctaButtonSx, border: 'none', cursor: 'pointer' }}
              >
                Save Changes
              </Box>
            </Box>
          </Box>
        )}
      </Dialog>

      {/* Case Study Edit Modal — edits the title plus the Scenario/Action/Relevance text, not the structure */}
      <Dialog
        open={!!csEdit}
        onClose={() => setCsEdit(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1A1A1A',
            borderRadius: '12px',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            p: 3
          }
        }}
      >
        {csEdit && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography
                sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}
              >
                {csEdit.mode === 'add' ? 'Add Case Study' : 'Edit Case Study'}
              </Typography>
              <Box
                component="button"
                onClick={() => setCsEdit(null)}
                sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0.5, flexShrink: 0 }}
              >
                <CloseIcon sx={{ color: 'var(--Text-text-secondary, #B6B6B6)', fontSize: 22 }} />
              </Box>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'Inter',
                  color: 'var(--Text-text-secondary, #B6B6B6)',
                  mb: 1
                }}
              >
                Title
              </Typography>
              <Box
                component="input"
                value={csEdit.title}
                placeholder={`Case Study ${csEdit.index + 1}`}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCsEdit((prev) => (prev ? { ...prev, title: e.target.value } : prev))
                }
                sx={{
                  width: '100%',
                  background: 'transparent',
                  border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                  borderRadius: '8px',
                  px: 1.5,
                  py: 1,
                  color: 'var(--Text-text-primary, #FDFDFD)',
                  fontFamily: 'Inter',
                  fontSize: 14,
                  lineHeight: '22px',
                  outline: 'none',
                  '&::placeholder': { color: 'var(--Text-text-tertiary, #868686)' },
                  '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                }}
              />
            </Box>

            {csEdit.sections.map((s, si) => (
              <Box key={s.label}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: 'Inter',
                    color: 'var(--Text-text-secondary, #B6B6B6)',
                    mb: 1
                  }}
                >
                  {s.label}
                </Typography>
                <Box
                  component="textarea"
                  value={s.text}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setCsEdit((prev) =>
                      prev
                        ? {
                            ...prev,
                            sections: prev.sections.map((x, xi) => (xi === si ? { ...x, text: e.target.value } : x))
                          }
                        : prev
                    )
                  }
                  rows={3}
                  sx={{
                    width: '100%',
                    background: 'transparent',
                    resize: 'vertical',
                    border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                    borderRadius: '8px',
                    px: 1.5,
                    py: 1,
                    color: 'var(--Text-text-primary, #FDFDFD)',
                    fontFamily: 'Inter',
                    fontSize: 14,
                    lineHeight: '22px',
                    outline: 'none',
                    '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                  }}
                />
              </Box>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 1 }}>
              <Box component="button" onClick={() => setCsEdit(null)} sx={{ ...secondaryButtonSx, cursor: 'pointer' }}>
                Cancel
              </Box>
              <Box
                component="button"
                onClick={() => {
                  // Blank title falls back to the positional default so a card is never untitled.
                  const title = csEdit.title.trim() || `Case Study ${csEdit.index + 1}`;
                  const next =
                    csEdit.mode === 'add'
                      ? [...caseStudies, { id: `cs-${Date.now()}`, title, sections: csEdit.sections }]
                      : caseStudies.map((cs, i) =>
                          i === csEdit.index ? { ...cs, title, sections: csEdit.sections } : cs
                        );
                  setCaseStudies(next);
                  persistSection({ caseStudies: next });
                  if (csEdit.mode === 'add') setExpandedCaseStudy(csEdit.index);
                  setCsEdit(null);
                }}
                sx={{ ...ctaButtonSx, border: 'none', cursor: 'pointer' }}
              >
                Save Changes
              </Box>
            </Box>
          </Box>
        )}
      </Dialog>

      {/* Section-scoped Improve with AI (Case Studies / Live Simulation / Quiz) — same modal, no
          regenerate banner. Regenerates ONLY the targeted section; BE always passes program name +
          structure + module + lesson content alongside the feedback. A case-study target narrows
          it further to that one card, so the other case studies are left as they are. */}
      <ImproveWithAIModal
        variant="section"
        open={sectionImproveTarget !== null}
        isLoading={sectionImproveLoading}
        entityLabel={sectionImproveTarget?.caseStudyIndex !== undefined ? 'Case Study' : undefined}
        entityName={
          sectionImproveTarget?.caseStudyIndex !== undefined
            ? caseStudies[sectionImproveTarget.caseStudyIndex]?.title
            : undefined
        }
        notice={
          // A quiz regenerate replaces every existing question (applyElliottSection
          // does a full setMcqs), and the section variant submits with an empty
          // prompt — so say what is about to be lost before the admin confirms.
          sectionImproveTarget?.section === 'quiz' && mcqs.length > 0
            ? `This regenerates the whole quiz and replaces your ${mcqs.length} existing question${
                mcqs.length === 1 ? '' : 's'
              }. To keep them, cancel and write the new question yourself.`
            : undefined
        }
        onClose={() => {
          if (!sectionImproveLoading) setSectionImproveTarget(null);
        }}
        onSubmit={(feedback) => handleSectionImprove(feedback)}
      />
    </Box>
  );
};

export default LessonContentView;
